import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Apartment, RegisterApartmentRequest, PhotoDto, PhotoRequest, BookingFlowMode } from '../types/apartment';
import { PhotoUploader } from './PhotoUploader';
import { PhotoGalleryEditor } from './PhotoGalleryEditor';
import { ConfirmDialog } from './ConfirmDialog';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';
import { ApartmentMapPreview } from './ApartmentMapPreview';
import { useUpdatePhotos } from '../hooks/useApartments';
import { is404 } from '../utils/errors';
import type { ResolvedPlaceDetails } from '../utils/extractAddressComponents';

const apartmentSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  street: z.string().min(3, 'Street is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().optional(),
  country: z.string().min(2, 'Country is required'),
  postalCode: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  // Google Places geocoding — set programmatically via autocomplete selection
  placeId: z.string().optional(),
  formattedAddress: z.string().optional(),
  bedrooms: z.number().min(0).int(),
  bathrooms: z.number().min(0),
  maxGuests: z.number().min(1).int(),
  sizeSquareMeters: z.number().min(1).optional(),
  pricePerNight: z.number().min(0),
  features: z.array(z.string()),
  photoUrls: z.array(z.string()).optional(),
  bookingFlowMode: z.enum(['IMMEDIATE_PAYMENT', 'DEFERRED_PAYMENT', 'DEFERRED_BANK_TRANSFER']).optional(),
  photos: z.array(z.object({
    key: z.string(),
    photoUrl: z.string(),
    thumbnailUrl: z.string().optional(),
    optimizedUrl: z.string().optional(),
    uploadedAt: z.string().optional(),
    displayOrder: z.number().int().optional(),
  })).optional(),
});

type ApartmentFormData = z.infer<typeof apartmentSchema>;

interface ApartmentFormProps {
  apartment?: Apartment;
  onSubmit: (data: RegisterApartmentRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/** Maps feature keys (e.g. WIFI) to their i18n key suffix (e.g. featureWifi). */
const featureKey = (feature: string): string => {
  const camel = feature.toLowerCase().replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  return `badges:feature${camel.charAt(0).toUpperCase() + camel.slice(1)}`;
};

const AVAILABLE_FEATURES = [
  'WIFI',
  'AIR_CONDITIONING',
  'HEATING',
  'KITCHEN',
  'WASHING_MACHINE',
  'DRYER',
  'TV',
  'PARKING',
  'ELEVATOR',
  'BALCONY',
  'TERRACE',
  'GARDEN',
  'POOL',
  'GYM',
  'PETS_ALLOWED',
  'SMOKING_ALLOWED',
  'WHEELCHAIR_ACCESSIBLE',
  'FAMILY_FRIENDLY',
  'WORK_FRIENDLY',
];

// Helper to flatten apartment for form
const flattenApartment = (apartment: Apartment): ApartmentFormData => ({
  name: apartment.name,
  description: apartment.description,
  street: apartment.address.street,
  city: apartment.address.city,
  state: apartment.address.state,
  country: apartment.address.country,
  postalCode: apartment.address.postalCode,
  latitude: apartment.address.latitude,
  longitude: apartment.address.longitude,
  // Carry geocoding data into form state for pre-populated map + autocomplete display
  placeId: apartment.address.placeId ?? undefined,
  formattedAddress: apartment.address.formattedAddress ?? undefined,
  bedrooms: apartment.details.bedrooms,
  bathrooms: apartment.details.bathrooms,
  maxGuests: apartment.details.maxGuests,
  sizeSquareMeters: apartment.details.sizeSquareMeters,
  pricePerNight: apartment.details.pricePerNight,
  features: apartment.details.features,
  photoUrls: apartment.photos.map(p => p.photoUrl),
  bookingFlowMode: apartment.bookingFlowMode,
  photos: apartment.photos,
});

export const ApartmentForm = ({ apartment, onSubmit, onCancel, isLoading }: ApartmentFormProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ApartmentFormData>({
    resolver: zodResolver(apartmentSchema),
    defaultValues: apartment ? flattenApartment(apartment) : {
      name: '',
      description: '',
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      latitude: undefined,
      longitude: undefined,
      placeId: undefined,
      formattedAddress: undefined,
      bedrooms: 1,
      bathrooms: 1,
      maxGuests: 2,
      sizeSquareMeters: 50,
      pricePerNight: 100,
      features: [],
      photoUrls: [],
      bookingFlowMode: undefined,
      photos: [],
    },
  });

  // ── Places autocomplete handlers ──────────────────────────────────────────
  const handlePlaceSelected = (place: ResolvedPlaceDetails) => {
    setValue('placeId', place.placeId);
    setValue('formattedAddress', place.formattedAddress);
    setValue('latitude', place.latitude);
    setValue('longitude', place.longitude);
    setValue('street', place.street);
    setValue('city', place.city);
    setValue('state', place.state);
    setValue('country', place.country);
    setValue('postalCode', place.postalCode);
  };

  const handleAddressCleared = () => {
    setValue('placeId', undefined);
    setValue('formattedAddress', undefined);
    setValue('latitude', undefined);
    setValue('longitude', undefined);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── photo delete state (edit mode only) ──────────────────────────────────
  const updatePhotosMutation = useUpdatePhotos();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── last-photo confirm dialog state ──────────────────────────────────────
  const [confirmLastPhoto, setConfirmLastPhoto] = useState<{
    open: boolean;
    photo: PhotoDto | null;
  }>({ open: false, photo: null });

  const doDeletePhoto = async (photoToRemove: PhotoDto) => {
    if (!apartment) return;
    const currentPhotos = watch('photos') ?? [];
    const remaining: PhotoRequest[] = currentPhotos
      .filter(p => p.key !== photoToRemove.key)
      .map((p, index) => ({
        key: p.key,
        photoUrl: p.photoUrl,
        thumbnailUrl: p.thumbnailUrl,
        optimizedUrl: p.optimizedUrl,
        uploadedAt: p.uploadedAt,
        // Preserve existing displayOrder; fall back to array index
        displayOrder: p.displayOrder ?? index,
      }));
    setDeleteError(null);

    try {
      await updatePhotosMutation.mutateAsync({ id: apartment.id, photos: remaining });
      setValue('photos', remaining);
      setValue('photoUrls', remaining.map(p => p.photoUrl));
    } catch (err) {
      setDeleteError(
        is404(err) ? t('errors:apartmentNotFound') : t('errors:somethingWentWrong')
      );
    }
  };

  const handleDeletePhoto = (photoToRemove: PhotoDto) => {
    if (!apartment) return;
    const currentPhotos = watch('photos') ?? [];

    // AC-9: last-photo confirmation guard
    if (currentPhotos.length === 1) {
      setConfirmLastPhoto({ open: true, photo: photoToRemove });
      return;
    }

    void doDeletePhoto(photoToRemove);
  };
  // ─────────────────────────────────────────────────────────────────────────

  /** Auto-saves updated photo order (displayOrder values) for all photos.
   *  Snapshots current photos before the call so the UI can revert on failure. */
  const handlePhotosChange = async (updatedPhotos: PhotoRequest[]) => {
    if (!apartment) return;
    const snapshot = watch('photos') ?? [];
    setDeleteError(null);
    try {
      await updatePhotosMutation.mutateAsync({ id: apartment.id, photos: updatedPhotos });
      setValue('photos', updatedPhotos as PhotoDto[]);
      setValue('photoUrls', updatedPhotos.map(p => p.photoUrl));
    } catch (err) {
      // Revert to last successfully saved order
      setValue('photos', snapshot);
      setDeleteError(
        is404(err) ? t('errors:apartmentNotFound') : t('errors:somethingWentWrong')
      );
    }
  };

  const selectedFeatures = watch('features') || [];
  const photoUrls = watch('photoUrls') || [];
  const editPhotos = watch('photos') || [];

  const toggleFeature = (feature: string) => {
    const current = selectedFeatures;
    if (current.includes(feature)) {
      setValue('features', current.filter((f) => f !== feature));
    } else {
      setValue('features', [...current, feature]);
    }
  };

  const watchedPlaceId = watch('placeId')
  const watchedLat = watch('latitude')
  const watchedLng = watch('longitude')
  const watchedFormattedAddress = watch('formattedAddress')

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('forms:basicInformation')}</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:name')}</label>
            <input
              {...register('name')}
              className="input"
              placeholder="Cozy Downtown Loft"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{t('errors:nameMin')}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:description')}</label>
            <textarea
              {...register('description')}
              className="input"
              rows={4}
              placeholder="Beautiful modern loft in the heart of the city"
            />
            {errors.description && <p className="text-red-500 text-sm mt-1">{t('errors:descriptionMin')}</p>}
          </div>
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('forms:location')}</h3>

          {/* Address autocomplete search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:addressSearch')}</label>
            <AddressAutocompleteInput
              initialValue={watchedFormattedAddress}
              onPlaceSelected={handlePlaceSelected}
              onCleared={handleAddressCleared}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500 mt-1">{t('forms:addressSearchHint')}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:street')}</label>
              <input {...register('street')} className="input" placeholder="123 Main Street" />
              {errors.street && <p className="text-red-500 text-sm mt-1">{t('errors:streetRequired')}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:city')}</label>
              <input {...register('city')} className="input" placeholder="Berlin" />
              {errors.city && <p className="text-red-500 text-sm mt-1">{t('errors:cityRequired')}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:state')}</label>
              <input {...register('state')} className="input" placeholder="BE" />
              {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:country')}</label>
              <input {...register('country')} className="input" placeholder="Germany" />
              {errors.country && <p className="text-red-500 text-sm mt-1">{t('errors:countryRequired')}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:postalCode')}</label>
              <input {...register('postalCode')} className="input" placeholder="10115" />
              {errors.postalCode && <p className="text-red-500 text-sm mt-1">{errors.postalCode.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:latitude')}</label>
              <input
                {...register('latitude', { valueAsNumber: true })}
                type="number"
                step="any"
                className="input"
                placeholder="52.5200"
              />
              {errors.latitude && <p className="text-red-500 text-sm mt-1">{errors.latitude.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:longitude')}</label>
              <input
                {...register('longitude', { valueAsNumber: true })}
                type="number"
                step="any"
                className="input"
                placeholder="13.4050"
              />
              {errors.longitude && <p className="text-red-500 text-sm mt-1">{errors.longitude.message}</p>}
            </div>
          </div>

          {/* Map preview — only shown when geocoding data is present */}
          {watchedPlaceId != null && watchedLat != null && watchedLng != null && (
            <ApartmentMapPreview lat={watchedLat} lng={watchedLng} />
          )}
        </div>

        {/* Property Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('forms:propertyDetails')}</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bedrooms')}</label>
              <input
                {...register('bedrooms', { valueAsNumber: true })}
                type="number"
                min="0"
                className="input"
              />
              {errors.bedrooms && <p className="text-red-500 text-sm mt-1">{errors.bedrooms.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bathrooms')}</label>
              <input
                {...register('bathrooms', { valueAsNumber: true })}
                type="number"
                min="0"
                step="0.5"
                className="input"
              />
              {errors.bathrooms && <p className="text-red-500 text-sm mt-1">{errors.bathrooms.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:maxGuests')}</label>
              <input
                {...register('maxGuests', { valueAsNumber: true })}
                type="number"
                min="1"
                className="input"
              />
              {errors.maxGuests && <p className="text-red-500 text-sm mt-1">{errors.maxGuests.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:size')}</label>
              <input
                {...register('sizeSquareMeters', { valueAsNumber: true })}
                type="number"
                min="1"
                step="0.1"
                className="input"
              />
              {errors.sizeSquareMeters && <p className="text-red-500 text-sm mt-1">{errors.sizeSquareMeters.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:pricePerNight')}</label>
              <input
                {...register('pricePerNight', { valueAsNumber: true })}
                type="number"
                min="0"
                step="0.01"
                className="input"
              />
              {errors.pricePerNight && <p className="text-red-500 text-sm mt-1">{errors.pricePerNight.message}</p>}
            </div>
          </div>
        </div>

        {/* Booking Flow Mode */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('forms:bookingFlowMode')}</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bookingFlowMode')}</label>
            <select
              {...register('bookingFlowMode')}
              className="input"
              defaultValue={apartment?.bookingFlowMode ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setValue('bookingFlowMode', val === '' ? undefined : val as BookingFlowMode);
              }}
            >
              <option value="" disabled>—</option>
              <option value="IMMEDIATE_PAYMENT">{t('forms:bookingFlowMode_immediatePayment')}</option>
              <option value="DEFERRED_PAYMENT">{t('forms:bookingFlowMode_deferredPayment')}</option>
              <option value="DEFERRED_BANK_TRANSFER">{t('forms:bookingFlowMode_deferredBankTransfer')}</option>
            </select>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('forms:features')}</h3>
          <div className="grid grid-cols-3 gap-3">
            {AVAILABLE_FEATURES.map((feature) => (
              <label
                key={feature}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedFeatures.includes(feature)
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedFeatures.includes(feature)}
                  onChange={() => toggleFeature(feature)}
                  className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                />
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <span className="text-sm font-medium text-gray-700">{t(featureKey(feature) as any)}</span>
              </label>
            ))}
          </div>
          {errors.features && <p className="text-red-500 text-sm mt-1">{errors.features.message}</p>}
        </div>

        {/* Photos */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">{t('forms:photos')}</h3>
          </div>

          {/* Info message for new apartments */}
          {!apartment && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                {t('forms:createApartmentFirst')}
              </p>
            </div>
          )}

          {/* Photo uploader - only show for existing apartments */}
          {apartment && (
            <div className="mb-4">
              <PhotoUploader
                apartmentId={apartment.id}
                nextDisplayOrder={editPhotos.length}
                onPhotoUploaded={(photoData) => {
                  const newPhoto: PhotoDto = {
                    key: photoData.key,
                    photoUrl: photoData.photoUrl,
                    thumbnailUrl: photoData.thumbnailUrl,
                    optimizedUrl: photoData.optimizedUrl,
                    displayOrder: photoData.displayOrder,
                  };
                  setValue('photos', [...editPhotos, newPhoto]);
                  setValue('photoUrls', [...photoUrls, photoData.photoUrl]);
                }}
                maxSizeMB={5}
              />
            </div>
          )}

          {/* Photo gallery with drag-and-drop reordering */}
          {apartment ? (
            <>
              <PhotoGalleryEditor
                apartmentId={apartment.id}
                photos={editPhotos}
                onPhotosChange={(updatedPhotos) => void handlePhotosChange(updatedPhotos)}
                onDeletePhoto={(photo) => handleDeletePhoto(photo)}
              />
              {deleteError && (
                <p className="text-sm text-red-600 mt-2">{deleteError}</p>
              )}
            </>
          ) : (
            photoUrls.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photoUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Apartment photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setValue('photoUrls', photoUrls.filter((_, i) => i !== index))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
                      {url.split('/').pop()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">{t('forms:photosAfterCreate')}</p>
            )
          )}
          {errors.photoUrls && <p className="text-red-500 text-sm mt-1">{errors.photoUrls.message}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
            {t('common:cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading
              ? t('common:saving')
              : apartment
              ? t('pages:updateApartment')
              : t('pages:createApartment')}
          </button>
        </div>
      </form>

      {/* Last-photo confirmation dialog */}
      <ConfirmDialog
        open={confirmLastPhoto.open}
        message={t('pages:removeLastPhotoConfirm')}
        intent="danger"
        onConfirm={() => {
          const photo = confirmLastPhoto.photo;
          setConfirmLastPhoto({ open: false, photo: null });
          if (photo) void doDeletePhoto(photo);
        }}
        onCancel={() => setConfirmLastPhoto({ open: false, photo: null })}
      />
      </>
    </APIProvider>
  );
};
